<script setup lang="ts">
  import { computed } from 'vue'
  import { ChevronLeft, ChevronRight, Repeat } from 'lucide-vue-next'
  import type {
    SuperUserQuestionnaire as Questionnaire,
    QuestionnaireSection,
    QuestionnaireQuestion
  } from '@/types/generated'
  import { useQuestionnaireEditorStore } from '@/store/admin/questionnaire/questionnaireEditor'
  import { useTranslation } from '@/composables/useTranslation'
  import { Switch } from '@/components/ui/switch'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import { entitiesDE, entitiesEN } from '@/i18n/entity'
  import { getEntityAccusativeDE } from '@/i18n/entityAccusative'
  import VirtualQuestionContent from './VirtualQuestionContent.vue'

  const { t, locale } = useTranslation({
    en: {
      previousQuestion: 'Previous question',
      nextQuestion: 'Next question',
      question: 'Question',
      required: 'Required',
      virtualQuestionTitle: 'Would you like to add another {participantType}?',
      virtualQuestionTitleFallback: 'Would you like to add another participant?',
      autoGeneratedTooltip:
        'This question was created automatically. If you want to remove it, turn off the "Repeatable" option.',
      untitledSection: 'Untitled Section'
    },
    de: {
      previousQuestion: 'Vorherige Frage',
      nextQuestion: 'Nächste Frage',
      question: 'Frage',
      required: 'Erforderlich',
      virtualQuestionTitle: 'Möchten Sie {accusativePhrase} hinzufügen?',
      virtualQuestionTitleFallback: 'Möchten Sie einen weiteren Teilnehmer hinzufügen?',
      autoGeneratedTooltip:
        'Diese Frage wurde automatisch erstellt. Wenn Sie sie entfernen möchten, deaktivieren Sie die Option "Wiederholbar".',
      untitledSection: 'Unbenannter Abschnitt'
    }
  })

  const props = defineProps<{
    questionnaire: Questionnaire
    sectionUuid: string
  }>()

  const questionnaireEditorStore = useQuestionnaireEditorStore()

  // Get the section for this virtual question
  const section = computed(() =>
    props.questionnaire.schema?.find((s) => s.uuid === props.sectionUuid)
  )

  // Get the display name for the participant type
  const participantName = computed<string | null>(() => {
    if (!section.value || (section.value as any).type !== 'participant') return null
    const participantType = (section.value as any).participant_type
    if (!participantType) return null

    const entityTranslations = locale.value === 'de' ? entitiesDE : entitiesEN
    return entityTranslations[participantType as keyof typeof entityTranslations] || participantType
  })

  // Get the raw participant type for accusative lookup
  const participantType = computed<string | null>(() => {
    if (!section.value || (section.value as any).type !== 'participant') return null
    return (section.value as any).participant_type || null
  })

  // Get the virtual question title
  const virtualQuestionTitle = computed<string>(() => {
    if (!participantName.value || !participantType.value) {
      return t('virtualQuestionTitleFallback')
    }
    if (locale.value === 'de') {
      const accusativePhrase = getEntityAccusativeDE(participantType.value)
      if (!accusativePhrase) {
        return t('virtualQuestionTitleFallback')
      }
      return t('virtualQuestionTitle', { accusativePhrase })
    }
    return t('virtualQuestionTitle', { participantType: participantName.value })
  })

  // Get all questions including virtual questions in order for navigation
  const allQuestions = computed(() => {
    const questions: {
      type: 'real' | 'virtual'
      question?: QuestionnaireQuestion
      section: QuestionnaireSection
    }[] = []

    for (const sec of props.questionnaire.schema || []) {
      // Add real questions
      for (const question of sec.items || []) {
        questions.push({ type: 'real', question, section: sec })
      }
      // Add virtual question if section is repeatable with participant type
      if (
        (sec as any).repeatable &&
        (sec as any).type === 'participant' &&
        (sec as any).participant_type
      ) {
        questions.push({ type: 'virtual', section: sec })
      }
    }

    return questions
  })

  // Find current question index (the virtual question for this section)
  const currentQuestionIndex = computed(() => {
    return allQuestions.value.findIndex(
      (item) => item.type === 'virtual' && item.section.uuid === props.sectionUuid
    )
  })

  // Get question number within section
  const questionNumberInSection = computed(() => {
    if (!section.value) return 0
    // Virtual question is always at the end, so its number is items.length + 1
    return (section.value.items?.length || 0) + 1
  })

  // Total questions in section (including virtual)
  const totalQuestionsInSection = computed(() => {
    if (!section.value) return 0
    return (section.value.items?.length || 0) + 1
  })

  // Navigation functions
  const goToPreviousQuestion = () => {
    const prevIndex = currentQuestionIndex.value - 1
    if (prevIndex >= 0) {
      const prevItem = allQuestions.value[prevIndex]
      if (prevItem.type === 'real' && prevItem.question) {
        questionnaireEditorStore.selectQuestion(prevItem.question.uuid)
      } else if (prevItem.type === 'virtual') {
        questionnaireEditorStore.selectVirtualQuestion(prevItem.section.uuid)
      }
    }
  }

  const goToNextQuestion = () => {
    const nextIndex = currentQuestionIndex.value + 1
    if (nextIndex < allQuestions.value.length) {
      const nextItem = allQuestions.value[nextIndex]
      if (nextItem.type === 'real' && nextItem.question) {
        questionnaireEditorStore.selectQuestion(nextItem.question.uuid)
      } else if (nextItem.type === 'virtual') {
        questionnaireEditorStore.selectVirtualQuestion(nextItem.section.uuid)
      }
    }
  }

  // Check if navigation is available
  const hasPreviousQuestion = computed(() => currentQuestionIndex.value > 0)
  const hasNextQuestion = computed(() => currentQuestionIndex.value < allQuestions.value.length - 1)
</script>

<template>
  <div
    v-if="section && participantName"
    class="flex h-full w-full flex-col"
  >
    <!-- Navigation Header -->
    <div
      class="inline-flex items-center justify-between self-stretch rounded-xl bg-white p-4
        shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200"
    >
      <div class="inline-flex flex-1 flex-col items-start justify-start gap-2.5">
        <button
          :disabled="!hasPreviousQuestion"
          class="inline-flex h-8 items-center justify-center overflow-hidden rounded-md bg-white p-1.5
            shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200
            hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          @click="goToPreviousQuestion"
        >
          <div class="relative h-5 w-5 overflow-hidden">
            <ChevronLeft class="absolute left-[4.17px] top-[4.17px] h-3 w-3" />
          </div>
          <div class="flex items-center justify-center gap-1.5 px-1.5">
            <div
              class="justify-start font-['Inter'] text-sm font-medium leading-tight text-gray-800"
            >
              {{ t('previousQuestion') }}
            </div>
          </div>
        </button>
      </div>

      <div class="inline-flex flex-1 flex-col items-center justify-start">
        <div
          class="justify-start font-['Inter'] text-base font-medium leading-normal text-gray-800"
        >
          {{ t('question') }} {{ questionNumberInSection }}/{{ totalQuestionsInSection }}
        </div>
        <div class="justify-start font-['Inter'] text-xs font-normal leading-none text-gray-600">
          {{ section.title || t('untitledSection') }}
        </div>
      </div>

      <div class="flex flex-1 items-center justify-end gap-2">
        <button
          :disabled="!hasNextQuestion"
          class="flex h-8 items-center justify-center overflow-hidden rounded-md bg-white p-1.5
            shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200
            hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          @click="goToNextQuestion"
        >
          <div class="flex items-center justify-center gap-1.5 px-1.5">
            <div
              class="justify-start font-['Inter'] text-sm font-medium leading-tight text-gray-800"
            >
              {{ t('nextQuestion') }}
            </div>
          </div>
          <div class="relative h-5 w-5 overflow-hidden">
            <ChevronRight class="absolute left-[4.17px] top-[4.17px] h-3 w-3" />
          </div>
        </button>
      </div>
    </div>

    <!-- Virtual Question Content Card -->
    <div
      class="mt-2 flex flex-col items-start justify-start self-stretch overflow-hidden rounded-xl bg-white
        shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200"
    >
      <!-- Question Header -->
      <div
        class="inline-flex h-16 items-center justify-start gap-2 self-stretch border-b border-gray-200 bg-white p-4"
      >
        <!-- Repeat indicator with tooltip -->
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger as-child>
              <div class="flex items-center justify-center">
                <Repeat class="h-5 w-5 text-gray-400" />
              </div>
            </TooltipTrigger>
            <TooltipContent
              variant="secondary"
              :show-arrow="true"
              class="max-w-xs"
            >
              <p>{{ t('autoGeneratedTooltip') }}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <div class="flex flex-1 items-center justify-start gap-2">
          <span class="flex-1 font-['Inter'] text-lg font-semibold leading-7 text-gray-800">
            {{ virtualQuestionTitle }}
          </span>
        </div>

        <div class="flex items-center justify-start gap-4">
          <!-- Required toggle (always on, disabled for virtual question) -->
          <div class="flex items-center justify-start gap-2">
            <div
              class="justify-start font-['Inter'] text-sm font-medium leading-tight text-gray-800"
            >
              {{ t('required') }}
            </div>
            <Switch
              :checked="true"
              disabled
            />
          </div>
        </div>
      </div>

      <!-- Question Content -->
      <div class="flex w-full flex-col self-stretch p-4">
        <VirtualQuestionContent :question-title="virtualQuestionTitle" />
      </div>
    </div>
  </div>
</template>
